import { Language } from './language'
import type { Snippet } from './snippet'
import * as _snippets from './snippets'

/**
 * Contains a set of properties that are used to control the language, syntax
 * highlighting, code snippets / auto-completion, and other related
 * {@link monaco} features for a {@link monaco.editor.IStandaloneCodeEditor}
 */
export class Syntax {
  /**
   * Tracks all existing {@link Syntax} by their {@link Syntax.id}
   *
   * Used in {@link of}
   */
  private static readonly _instancesById: Partial<Record<string, Syntax>> = {}

  /* ---------------------------- Standard Syntaxes --------------------------- */

  /**
   * {@link Syntax} with no specified language or features
   */
  public static readonly NONE = new Syntax('none')

  /**
   * JSON {@link Syntax} specifically for mosaic widget rendering configuration.
   *
   * __NOTE:__ This static field is essential as it initializes the `mosaic-widget-rendering`
   * JSON language with the corresponding schema validation
   */
  public static readonly MOSAIC_WIDGET_RENDERING = new Syntax(
    'mosaic-widget-rendering',
    {
      jsonSchema: 'core/mosaic/mosaic-widget-rendering.schema.json',
      // NOTE: this needs to be defined in order for the Monaco editor to
      // properly highlight the JSON schema
      language: 'json',
    },
  )

  /**
   * __NOTE:__ This static field is essential as it initializes the `json` language
   * with the corresponding schema validation for core DMS query syntax.
   */
  public static readonly JSON = new Syntax('json', {
    jsonSchema: 'core/dms/data/query/query.schema.json',
  })

  /* -------------------------------- Factories ------------------------------- */

  /**
   * Gets the existing {@link Syntax} with the given ID, if there is one.
   * Otherwise, it assumes the ID represents a language and creates a new
   * {@link Syntax} for the given language
   */
  public static of(idOrLanguage: string | undefined | Language): Syntax {
    if (idOrLanguage === undefined) {
      return Syntax.NONE
    }

    const syntaxId =
      idOrLanguage instanceof Language ? idOrLanguage.name : idOrLanguage
    // Return existing Syntax instance if exists, or create new based on the
    // passed syntax id
    return (this._instancesById[syntaxId] ??= new Syntax(syntaxId, {
      language: syntaxId,
    }))
  }

  /* -------------------------------- Instance -------------------------------- */

  /**
   * Language provided to {@link monaco} to determine appropriate syntax
   * highlighting and other helpful features.
   *
   * __NOTE__: If `undefined`, we will use default monaco language (e.g. 'plaintext').
   */
  public readonly language?: Language | undefined

  /**
   * If this {@link MonacoManager} is using a JSON schema for validation, this
   * is the URI of the schema being used for validation
   */
  public readonly jsonSchemaUri?: string

  /**
   * {@link https://code.visualstudio.com/docs/editor/userdefinedsnippets Code snippets}
   * that will be made available to the user to enable quick insertion of
   * boilerplate code
   *
   * @see {@link Snippet}
   */
  public readonly snippets: readonly Snippet[]

  constructor(
    public readonly id: string,
    {
      jsonSchema,
      language = jsonSchema ? 'json' : undefined,
      snippets = [],
    }: Syntax.constructor.Options = {},
  ) {
    Syntax._instancesById[this.id] = this

    this.language =
      // If the language is already a Language instance, use it as is
      language instanceof Language
        ? language
        : // Otherwise, its a string, so we can try and obtain a Language
          // instance from it
          language
          ? Language.of(language)
          : undefined

    this.jsonSchemaUri = jsonSchema && _getJsonSchemaUri(jsonSchema)
    this.snippets = snippets
  }
}
export namespace Syntax {
  export namespace constructor {
    export interface Options {
      /**
       * Specify the language for this {@link Syntax} using either a
       * {@link Language} object or a string that corresponds to a monaco editor
       * built-in language or a custom language registered in
       * {@link Language.All}
       *
       * @see {@link monaco.editor.setModelLanguage}
       */
      language?: string | Language
      /**
       * URI or path to a JSON schema
       *
       * __NOTE:__ If it is a path, it is assumed to be relative to our standard folder of
       * JSON schemas
       */
      jsonSchema?: string
      /**
       * @see {@link Snippet}, {@link Syntax.snippets}
       */
      snippets?: readonly Snippet[]
    }
  }
}

/**
 * Standardizes a {@link Syntax.constructor.Options.jsonSchema} into a
 * {@link Syntax.jsonSchemaUri}
 */
function _getJsonSchemaUri(jsonSchemaUriOrPath: string): string {
  return jsonSchemaUriOrPath.includes(':')
    ? jsonSchemaUriOrPath
    : `${window.location.origin}/assets/json-schemas/${jsonSchemaUriOrPath}`
}
